import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Menu } from '@mantine/core';
import { Copy, Dots, Pencil, Plus, Stack, Trash } from 'tabler-icons-react';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import TableView from '../../common/TableView';
import { triggerNotification } from '../../../helpers/notification';
import HeaderView from '../../common/HeaderView';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppMenu from '../../common/AppMenu';
import {
  ECOM_PRODUCT_BUNDLE_TYPE_LIST,
  ECOM_PRODUCT_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import ConfirmModal from '../../common/ConfirmModal';

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Status',
    value: 'status',
    sortable: true
  },
  {
    label: 'Bundle type',
    value: 'bundle_type',
    sortable: true
  },
  {
    label: 'Products',
    value: 'product_count',
    sortable: true
  },
  {
    label: 'Store',
    value: 'store',
    sortable: true,
    isVisible: ({ filter }) => filter.showStoreBundles
  }
];

const VIEW_ACTIONS = [
  {
    label: 'Edit',
    value: VIEW_ACTIONS_ENUM.EDIT,
    section: 1,
    icon: <Pencil size={18} />
  },
  {
    label: 'Duplicate',
    value: VIEW_ACTIONS_ENUM.DUPLICATE,
    section: 1,
    icon: <Copy size={18} />
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.DELETE,
    icon: <Trash color="red" size={18} />,
    section: 2,
    color: 'red'
  }
];

const VendorBundleTable = () => {
  const fetchedPk = useRef(null);
  const navigate = useNavigate();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchPaginatedVendorBundles,
    deleteVendorBundles
  } = useContext(VendorContext);
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      showStoreBundles: false,
      fkEcomCategory: null,
      fkEcomProductStatus: null
    },
    (newFilter) =>
      fetchPaginatedVendorBundles(authState.pkEcomVendor, newFilter),
    state.paginatedEcomVendorBundles
  );

  const loading = !fetchedPk.current || dataLoading;
  const noBundlesCreated = !loading && totalNoFilterCount === 0;

  const hasFiltered =
    !!filter.search || !!filter.showStoreBundles || !!filter.storeName;

  useEffect(() => {
    if (authState.pkEcomVendor) {
      onRefresh();
      fetchedPk.current = authState.pkEcomVendor;
    }
  }, [authState.pkEcomVendor]);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: 'Product bundles',
          to: '/vendor/product-bundles'
        }
      ]}
      rightSection={
        !loading && (
          <Button
            color="dark"
            leftSection={<Plus size={18} />}
            onClick={() => navigate('/vendor/product-bundles/new')}
            radius="md"
            size="compact-md"
            style={{ fontSize: 14 }}
          >
            Create bundle
          </Button>
        )
      }
    >
      <TableView
        columns={TABLE_COLUMNS.filter(
          (c) => !c.isVisible || c.isVisible({ filter })
        )}
        disabled={noBundlesCreated && !hasFiltered}
        disabledContent={{
          title: 'Create your first bundle',
          description:
            'Product bundles are groups of products that can be sold together.',
          icon: <Stack color="#000" size={125} />,
          primaryButton: {
            label: 'Create product bundle',
            icon: <Plus size={18} />,
            link: '/vendor/product-bundles/new'
          }
        }}
        emptyMessage="No product bundles found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by name',
            value: filter.search || '',
            onChange: (value) => onFilterChange({ ...filter, search: value })
          },
          {
            key: 2,
            label: 'Status',
            type: 'select',
            value: filter.fkEcomProductStatus || null,
            placeholder: 'Filter by status',
            searchable: false,
            clearable: true,
            data: [
              { value: ECOM_PRODUCT_STATUS_ENUM.ACTIVE, label: 'Active' },
              { value: ECOM_PRODUCT_STATUS_ENUM.DRAFT, label: 'Draft' }
            ],
            onChange: (value) => {
              onFilterChange(
                {
                  ...filter,
                  fkEcomProductStatus: value
                },
                true
              );
            }
          },
          ...(authState.userData.isAdmin
            ? [
                {
                  key: 3,
                  label: 'Owner',
                  type: 'select',
                  value: filter.showStoreBundles ? 'store' : 'vendor',
                  placeholder: 'Filter by owner',
                  data: [
                    {
                      label: 'Vendor',
                      value: 'vendor'
                    },
                    {
                      label: 'Store',
                      value: 'store'
                    }
                  ],
                  onChange: (value) => {
                    onFilterChange(
                      {
                        ...filter,
                        showStoreBundles: value === 'store',
                        storeName: ''
                      },
                      true
                    );
                  }
                },
                ...(filter.showStoreBundles
                  ? [
                      {
                        key: 4,
                        label: 'Store',
                        placeholder: 'Filter by store',
                        value: filter.storeName || '',
                        onChange: (value) =>
                          onFilterChange({ ...filter, storeName: value })
                      }
                    ]
                  : [])
              ]
            : [])
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={loading}
        noSort
        onAction={(action, item) => {
          switch (action) {
            case VIEW_ACTIONS_ENUM.EDIT:
              navigate(`/vendor/product-bundles/${item.key}`);
              break;
            case VIEW_ACTIONS_ENUM.DUPLICATE:
              navigate(
                `/vendor/product-bundles/${item.key}/duplicate?from=product-bundles`
              );
              break;
            default:
              onOpenModal(action, [
                data.find((b) => b.pkEcomVendorBundle === item.key)
              ]);
              break;
          }
        }}
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={data.map((b) => ({
          key: b.pkEcomVendorBundle,
          actions: VIEW_ACTIONS,
          columns: [
            {
              key: 1,
              label: b.name,
              to: `/vendor/product-bundles/${b.pkEcomVendorBundle}`,
              component: Link,
              weight: 500,
              src:
                b.previewImage ||
                b.ecomVendorBundleProducts
                  .map((p) => p.previewImage)
                  .find(Boolean) ||
                null,
              showImage: true
            },
            {
              key: 2,
              ...(b.fkEcomProductStatus.toString() ===
              ECOM_PRODUCT_STATUS_ENUM.ACTIVE
                ? {
                    label: 'Active',
                    color: 'green'
                  }
                : {
                    label: 'Draft',
                    color: 'grey'
                  })
            },
            {
              key: 3,
              label:
                ECOM_PRODUCT_BUNDLE_TYPE_LIST.find(
                  (t) => t.value === b.fkEcomBundleType.toString()
                )?.label || 'Unknown type'
            },
            {
              key: 4,
              label: b.ecomVendorBundleProducts.length
            },
            ...(filter.showStoreBundles
              ? [
                  {
                    key: 5,
                    label: b.ecomStore?.name || 'N/A'
                  }
                ]
              : [])
          ]
        }))}
        selectActionSection={
          <AppMenu
            control={
              <ActionIcon color="#ced4da" size="lg" variant="outline">
                <Dots color="#000" />
              </ActionIcon>
            }
            styles={{ dropdown: { padding: 0 } }}
          >
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Trash size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(
                    VIEW_ACTIONS_ENUM.DELETE,
                    data.filter((b) =>
                      selectedRows.includes(b.pkEcomVendorBundle)
                    )
                  );
                }}
                style={{ color: 'red' }}
              >
                Delete bundles
              </Menu.Item>
            </AppStack>
          </AppMenu>
        }
        selectedRows={selectedRows}
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Product Bundles"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete bundles"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText style={{ fontSize: 16 }}>
              Are you sure you want to delete{' '}
              <b>
                {modalState.item?.length === 1
                  ? modalState.item[0]?.name
                  : `${modalState.item?.length} product bundles`}
              </b>
              ?
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action cannot be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteVendorBundles(
            authState.pkEcomVendor,
            modalState.item.map((i) => i.pkEcomVendorBundle),
            () => {
              triggerNotification(
                modalState.item.length === 1
                  ? 'Product bundle deleted.'
                  : 'Product bundles deleted.',
                'success'
              );
              onCloseModal();
              onRefresh();
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${
          modalState.item?.length === 1
            ? modalState.item[0]?.name
            : `${modalState.item?.length} product bundles`
        }?
        `}
      />
    </HeaderView>
  );
};

VendorBundleTable.propTypes = {};

export default VendorBundleTable;
